import type { Character } from "@cloudnest/horror-race-engine";
import type { CSSProperties } from "react";
import { CHARACTER_COLORS, CHARACTER_LABELS } from "../character-display";

/**
 * Shared race-board pawn primitive (T-D1).
 *
 * Pure presentational dot — color/shadow/size only, no positioning or animation
 * logic of its own. Consumers supply the screen-specific testid and any
 * positioning via `className`/`style`:
 * - in-game `RaceTrack` lane cell: defaults (14px, inset glow, `horror-pawn`).
 * - `RoundRecapView` recap board: 16px, thin ring, `round-recap-pawn`, and an
 *   absolute `left%` + `transition` passed through `style` for the slide.
 *
 * The testid is a prop on purpose — `horror-pawn` and `round-recap-pawn` are
 * distinct e2e contracts and must NOT be unified.
 */
interface PawnProps {
  readonly character: Character;
  /** Reported as `data-pos` (board cell index / continuous position). */
  readonly position: number;
  /** Diameter in px. */
  readonly size?: number;
  /** Screen-specific testid — stays distinct per consumer (e2e gate). */
  readonly testId?: string;
  /** Shadow treatment: in-game inset+outer glow, recap a thin dark ring. */
  readonly glow?: "glow" | "ring";
  /** Character-name tooltip (in-game only). */
  readonly showTitle?: boolean;
  /** Extra class names (e.g. positioning/motion-reduce in a recap lane). */
  readonly className?: string;
  /** Extra inline style (e.g. absolute left% + transition in a recap lane). */
  readonly style?: CSSProperties;
}

export function Pawn({
  character,
  position,
  size = 14,
  testId = "horror-pawn",
  glow = "glow",
  showTitle = true,
  className,
  style,
}: PawnProps) {
  const color = CHARACTER_COLORS[character];
  const boxShadow =
    glow === "glow"
      ? `inset 0 0 10px ${color}, 0 0 6px color-mix(in srgb, ${color} 50%, transparent)`
      : "0 0 0 1px rgba(0,0,0,0.4)";

  return (
    <div
      data-testid={testId}
      data-character={character}
      data-pos={position}
      title={showTitle ? CHARACTER_LABELS[character] : undefined}
      className={className ? `rounded-full ${className}` : "rounded-full"}
      style={{
        width: size,
        height: size,
        backgroundColor: color,
        boxShadow,
        ...style,
      }}
    />
  );
}
